import { Button, CircularProgress, Dialog, DialogActions, DialogTitle } from "@mui/material";
import { Link } from "react-router-dom";
import Cookies from "universal-cookie";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { displayToastErrorApi, displayToastSuccess } from "../../../Helpers/toastHelper";
import { createData } from "../../../Services/DataServices";
import { cartActions } from "../../../store/cart-slice";
import useGetData from "../../../Hooks/useGetData";
import CheckoutModal from "../CheckoutModal/CheckoutModal";

export interface CartItem {
  id: string;
  price_type: string;
  has_cover: boolean;
}

export interface StoreItems {
  id: string;
}

export interface StorageItem {
  id: string;
}

export interface CartState {
  items: CartItem[];
  address_id: string;
  payment_method: number;
  client_note: string;
  store_items: StoreItems;
  storage_items: StorageItem[];
  uPayment?: string;
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  title: string;
  confirmLabel: string;
  cancelLabel: string;
  paymentMethod?: number;
  uPaymentUrl?: string;
}

export interface SubmitButtonProps {
  onClick: () => void;
  loading: boolean;
  label: string;
  disabled?: boolean;
}



export const SubmitButton = ({ onClick, loading, label, disabled }: SubmitButtonProps) => (
  <Button
    onClick={onClick}
    disabled={loading || disabled}
    endIcon={loading && <CircularProgress size={20} />}
    sx={{
      alignSelf: "center",
      backgroundColor: "white",
      color: "#755798",
      textTransform: "none",
      fontSize: {
        xs: "16px",
        sm: "18px",
        md: "22px",
        lg: "28px",
      },
      borderRadius: "12px",
      width: {
        mobile: "160px",
        md: "100%",
      },
      "&:hover": {
        backgroundColor: "#E8D6FF",
      },
    }}
  >
    {label}
  </Button>
);


export const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  loading,
  title,
  confirmLabel,
  cancelLabel,
  paymentMethod,
  uPaymentUrl
}: ConfirmDialogProps) => {
  // const cartState = useSelector((state: any) => state.cart);
  const cookies = new Cookies();
  const token = cookies.get("token");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" style={{ textAlign: "unset" }}>
        {title}
      </DialogTitle>

      <DialogActions>
        {token ? (
          <Button
            onClick={onConfirm}
            color="primary"
            autoFocus
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : confirmLabel}
          </Button>
        ) : (
          <Link to="/login" color="primary">
            {loading ? <CircularProgress size={20} /> : confirmLabel}
          </Link>
        )}
        <Button onClick={onClose} color="error">
          {cancelLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


const OrderComponent = () => {
  const cartState = useSelector((state: { cart: CartState }) => state.cart);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [totalUsd, setTotalUsd] = useState("");

  const { data } = useGetData("settings/all", []);
  const confirmMessage = data?.settings;

  const prepareFormData = () => {
    const formData = new FormData();
    
    formData.append("address_id", cartState.address_id || "");
    formData.append("payment_method", String(cartState.payment_method));
    formData.append("client_note", cartState.client_note);

    cartState.items?.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][price_type]`, item.price_type);
      formData.append(`items[${index}][has_cover]`, String(item.has_cover));
    });

    if (cartState.store_items?.id) {
      formData.append("storage_id", cartState.store_items.id);
      cartState.storage_items?.forEach((item, index) => {
        formData.append(`storage_ids[${index}]`, item.id);
      });
    }

    return formData;
  };

  const handleSubmitOrder = async () => {
    if (!cartState.items.length) return;

    try {
      setLoading(true);
      const formData = prepareFormData();
      const response = await createData("order/create-from-cart", formData);

      setTotalUsd(response?.data?.Data?.total_cost_usd);

      if (response.status === 200) {
        if (cartState.payment_method === 1) {
          dispatch(cartActions.clearCart());
          displayToastSuccess(response?.data?.message);
          navigate("/purchases");
          return;
        }

        if (cartState.payment_method === 4 && response?.data?.IsSuccess) {
          dispatch(cartActions.setUPaymentUrl(response?.data?.Data));
          window.location.href = response?.data?.Data;
          return;
        }
        if (cartState?.payment_method !== 4) {
          dispatch(cartActions.handleOpenCheckOutModal());
        }
        return response;
        dispatch(cartActions.handleOpenCheckOutModal());
      } else {
        displayToastErrorApi(response?.data?.message);
      }
    } catch (error: any) {
      displayToastErrorApi(
        error?.response?.data?.errors ||
        error?.data?.message ||
        error?.response?.data?.message
      );
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  // const handleSubmitLastStep = () => {
  //   if (cartState.payment_method === 3) {
  //     dispatch(cartActions.handleOpenCheckOutModal());
  //     return;
  //   }
    
  //   if (cartState.items.length > 0) {
  //     handleSubmitOrder();
  //   }
  // };
  const handleSubmitLastStep = async () => {
    if (cartState?.payment_method === 3) {
      dispatch(cartActions.handleOpenCheckOutModal());
      return;
    } else {
      handleSubmitOrder();
    }
  };
  const isSubmitDisabled = 
    cartState.items.length === 0 || 
    !cartState.store_items;

  return (
    <>
      <SubmitButton
        onClick={() => setShowConfirm(true)}
        loading={loading}
        label={t("purchases.payment.next")}
        disabled={isSubmitDisabled}
      />

      {cartState.payment_method === 3 && 
        cartState.items.length > 0 && 
        cartState.store_items && (
        <CheckoutModal 
          handleSubmitOrder={handleSubmitOrder} 
          total_usd={totalUsd} 
        />
      )}

      <ConfirmDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleSubmitLastStep}
        loading={loading}
        title={i18n.language === "ar" ? 
          confirmMessage?.confirm_message_ar : 
          confirmMessage?.confirm_message_en
        }
        confirmLabel={t("notAvailable.confirm")}
        cancelLabel={t("notAvailable.cancel")}
        paymentMethod={cartState.payment_method}
        uPaymentUrl={cartState.uPayment}
      />
    </>
  );
};

export default OrderComponent;
